import React, { useEffect, useState } from "react";
import { DatePicker, Typography, message, theme } from "antd";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { formatCents, formatPeriod } from "../shared/money";
import PageToolbar from "../../../../design-system/PageToolbar";
import ResponsiveTable from "../../../../design-system/ResponsiveTable";
import { errorText } from "../api";
import * as api from "./api";

const BOLD = new Set(["GL", "RE", "GK", "BE", "EVS"]);

const BwaTab = () => {
  const { t } = useTranslation("accounting");
  const { token } = theme.useToken();
  const [month, setMonth] = useState(dayjs().subtract(1, "month"));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    api
      .getBwa({ year: month.year(), month: month.month() + 1 })
      .then(res => setData(res.data))
      .catch(error => message.error(errorText(error)))
      .finally(() => setLoading(false));
  }, [month]);

  const cell = (v, r) => (
    <span className="acct-num" style={{ fontWeight: BOLD.has(r.key) ? 600 : 400 }}>
      {formatCents(v)}
    </span>
  );
  const pct = v =>
    v === null || v === undefined ? "" : <span className="acct-num">{`${v.toFixed(1)} %`}</span>;

  return (
    <div>
      <PageToolbar
        filters={
          <>
            <DatePicker picker="month" value={month} onChange={v => v && setMonth(v)} />
            {data ? (
              <Typography.Text type="secondary">
                {t("Cumulative through")} {formatPeriod(data.upTo)}
              </Typography.Text>
            ) : null}
          </>
        }
      />
      <ResponsiveTable
        mobileLayout="table"
        className="acct-grid"
        size="small"
        loading={loading}
        pagination={false}
        rowKey="key"
        dataSource={data?.rows || []}
        rowClassName={r => (BOLD.has(r.key) ? "ant-table-row-selected" : "")}
        columns={[
          {
            title: t("Position"),
            dataIndex: "label",
            render: (v, r) => <span style={{ fontWeight: BOLD.has(r.key) ? 600 : 400 }}>{v}</span>
          },
          { title: month.format("MM/YYYY"), dataIndex: "month", align: "end", render: cell },
          {
            title: `${t("Cumulative")} ${month.year()}`,
            dataIndex: "cumulative",
            align: "end",
            render: cell
          },
          { title: t("% of total output"), dataIndex: "percent", align: "end", render: pct },
          {
            title: `${t("Previous year")} ${month.year() - 1}`,
            dataIndex: "previousYear",
            align: "end",
            render: cell
          },
          {
            title: t("% previous year"),
            dataIndex: "previousPercent",
            align: "end",
            render: pct
          },
          {
            title: t("Change"),
            dataIndex: "change",
            align: "end",
            render: v => (
              <span className="acct-num" style={{ color: v < 0 ? token.colorError : undefined }}>
                {formatCents(v)}
              </span>
            )
          }
        ]}
      />
      <Typography.Paragraph type="secondary" style={{ marginTop: 8 }}>
        {t(
          "Layout of the DATEV standard BWA (short-term income statement). Revenue and neutral income positive, costs positive; the result is revenue minus costs."
        )}
      </Typography.Paragraph>
    </div>
  );
};

export default BwaTab;
